import React from 'react';
import Module from 'Components/Core/Module';
import useBrokerageConnections from 'Components/Banking/useBrokerageConnections';
import BrokerageLoginForm from 'Components/Banking/BrokerageLoginForm';
import BrokerageOrderForm from 'Components/Banking/BrokerageOrderForm';
import './Brokerage.css';

const emptyOrder = (depotId, settlementAccountId) => ({ depotId, settlementAccountId, instrumentId: '', venueId: '', side: 'BUY', quantity: '', limitPrice: '' });

// Uebersetzungen gemaess offizieller comdirect-Doku (Kapitel 7.2.3 Order).
const ORDER_STATUS_LABELS = {
	PENDING: 'In Bearbeitung',
	OPEN: 'Offen',
	EXECUTED: 'Ausgeführt',
	SETTLED: 'Abgerechnet',
	CANCELLED_USER: 'Gestrichen',
	EXPIRED: 'Verfallen',
	CANCELLED_SYSTEM: 'Abgelehnt',
	CANCELLED_TRADE: 'Storniert',
	UNKNOWN: 'Unbekannt',
	PARTIALLY_EXECUTED: 'Teilausgeführt',
	WAITING: 'Wartend',
};

const ORDER_TYPE_LABELS = {
	MARKET: 'Market',
	LIMIT: 'Limit',
	QUOTE: 'Quote',
	STOP_MARKET: 'Stop Market',
	STOP_LIMIT: 'Stop Limit',
	TRAILING_STOP_MARKET: 'Trailing Stop Market',
	TRAILING_STOP_LIMIT: 'Trailing Stop Limit',
	ONE_CANCELS_OTHER: 'OCO',
	NEXT_ORDER: 'Next Order',
};

function formatValidity(order) {
	if (order.validityType === 'GTD' && order.validity) {
		// order.validity kommt von comdirect im Format YYYY-MM-DD.
		const [year, month, day] = order.validity.split('-');
		return `${day}.${month}.${year}`;
	}
	if (order.validityType === 'GFD') return 'Tagesgültig';
	return order.validityType || '';
}

function formatAmount(amountValue) {
	if (!amountValue || amountValue.value === undefined) return '';
	return amountValue.value + (amountValue.unit && amountValue.unit !== 'XXX' ? ' ' + amountValue.unit : '');
}

// Muss (wie ModuleTable es intern tut) in <Module> gewrappt werden - nur das
// registriert bei x.wam[id].show()/hide() die tatsaechliche Ein-/Ausblende-
// Logik beim Seitenwechsel. Ohne diesen Wrapper bleibt die Seite dauerhaft im
// DOM sichtbar und ueberlappt beim Navigieren mit anderen Modulen.
export default function BrokerageOrderbook(props) {

	const x = props.x;
	const connections = useBrokerageConnections(x);

	const [connectionId, setConnectionId] = React.useState(null);
	const [loggedIn, setLoggedIn] = React.useState(false);
	// Unterscheidet "noch nicht geprueft" von "geprueft, aber nicht eingeloggt" -
	// ohne das wuerde bei jedem Reload kurz die Login-Maske aufblitzen, obwohl
	// der comdirect-Token in $_SESSION serverseitig oft noch bis zu ~10 Minuten
	// gueltig ist (siehe [[project_banking_module]]).
	const [sessionChecked, setSessionChecked] = React.useState(false);
	const [depots, setDepots] = React.useState(null);
	const [depotId, setDepotId] = React.useState(null);
	const [orders, setOrders] = React.useState(null);
	const [showOrderForm, setShowOrderForm] = React.useState(false);
	const [error, setError] = React.useState('');

	// Erste Verbindung automatisch vorauswaehlen, statt eine unnoetige Auswahl
	// zu erzwingen - umschalten auf eine andere geht danach jederzeit ueber den
	// Verbindungs-Wechsler (switchConnection()), wie in BrokerageOverview.js.
	React.useEffect(() => {
		if (connectionId === null && connections && connections.length > 0) {
			setConnectionId(connections[0].connectionid);
		}
	}, [connections, connectionId]);

	// Zwischen Verbindungen wechseln, ohne eine bereits gueltige comdirect-
	// Sitzung der ZIEL-Verbindung zu verwerfen - siehe BrokerageOverview.js.
	function switchConnection(id) {
		if (id === connectionId) return;
		setSessionChecked(false);
		setLoggedIn(false);
		setDepots(null);
		setDepotId(null);
		setOrders(null);
		setError('');
		setConnectionId(id);
	}

	function loadDepots() {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageDepotList', { connectionId }, (result) => {
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				setSessionChecked(true);
				if (content.status === 'login-required') {
					setLoggedIn(false);
					return;
				}
				setError(content.message || 'Depot konnte nicht geladen werden.');
				return;
			}
			setSessionChecked(true);
			setLoggedIn(true);
			const loaded = content.depots || [];
			setDepots(loaded);
			if (loaded.length === 1) setDepotId(loaded[0].depotId);
		});
	}

	function loadOrders() {
		x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageOrderList', { connectionId, depotId }, (result) => {
			const content = result.data.content || {};
			if (result.data.returncode !== 1) {
				if (content.status === 'login-required') {
					setLoggedIn(false);
					return;
				}
				setError(content.message || 'Orderbuch konnte nicht geladen werden.');
				return;
			}
			setOrders(content.orders || []);
		});
	}

	// Beim (erneuten) Setzen der Verbindung erst pruefen, ob am Server noch eine
	// gueltige comdirect-Sitzung liegt, statt direkt die Login-Maske zu zeigen -
	// loadDepots() setzt loggedIn/sessionChecked je nach Ergebnis selbst.
	React.useEffect(() => {
		if (connectionId !== null) loadDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [connectionId]);

	// Nach erfolgreichem Login (BrokerageLoginForm) direkt nachladen.
	React.useEffect(() => {
		if (loggedIn && depots === null) loadDepots();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn]);

	React.useEffect(() => {
		if (loggedIn && depotId) loadOrders();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn, depotId]);

	// Haelt die comdirect-Sitzung per refresh_token am Leben, solange diese
	// Kachel offen bleibt (auch im Hintergrund) - dasselbe Muster wie in
	// BrokerageOverview.js. Fehlte hier bisher komplett, obwohl ein Login auch
	// direkt im Orderbuch startet, nicht nur ueber die Depotuebersicht (Chat
	// vom 2026-07-25).
	React.useEffect(() => {
		if (!loggedIn || connectionId === null) return;
		const intervalId = setInterval(() => {
			x.was.axPost('Backend/webapp.php?module=Banking&task=BrokerageRefreshSession', { connectionId }, (result) => {
				if (result.data.returncode !== 1) {
					setLoggedIn(false);
					setSessionChecked(true);
				}
			});
		}, 8 * 60 * 1000);
		return () => clearInterval(intervalId);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [loggedIn, connectionId]);

	const connection = connections ? connections.find((c) => c.connectionid === connectionId) : null;

	let inner;
	if (connections === null) {
		inner = <p>Lade…</p>;
	} else if (connections.length === 0) {
		inner = <p>Keine comdirect-Verbindung vorhanden. Bitte zuerst unter Banking → Konten eine comdirect-Verbindung anlegen.</p>;
	} else if (connectionId === null) {
		inner = (
			<div className="brokerage-picker">
				{connections.map((c) => (
					<div key={c.connectionid} className="brokerage-picker-item" onClick={() => setConnectionId(c.connectionid)}>
						{c.bankname} – {c.accountlabel}
					</div>
				))}
			</div>
		);
	} else if (!sessionChecked) {
		inner = <p>Prüfe Sitzung…</p>;
	} else if (!loggedIn) {
		inner = (
			<BrokerageLoginForm
				x={x}
				connectionId={connectionId}
				accountLabel={connection ? connection.bankname + ' – ' + connection.accountlabel : ''}
				pinUse={true}
				pinStored={connection ? parseInt(connection.pinstored, 10) === 1 : false}
				onLoggedIn={() => setLoggedIn(true)}
			/>
		);
	} else if (depotId === null) {
		inner = (
			<React.Fragment>
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				{depots === null && <p>Lade Depot…</p>}
				{depots !== null && depots.length === 0 && <p>Kein Depot gefunden.</p>}
				<div className="brokerage-picker">
					{(depots || []).map((depot) => (
						<div key={depot.depotId} className="brokerage-picker-item" onClick={() => setDepotId(depot.depotId)}>
							Depot {depot.depotDisplayId}
						</div>
					))}
				</div>
			</React.Fragment>
		);
	} else {
		inner = (
			<React.Fragment>
				{error !== '' && <p className="brokerage-error"><i className="bi bi-exclamation-triangle"></i> {error}</p>}
				{!showOrderForm &&
					<button type="button" className="btn" onClick={() => setShowOrderForm(true)}>Neue Order</button>
				}
				{showOrderForm &&
					<BrokerageOrderForm
						x={x}
						connectionId={connectionId}
						initialOrder={emptyOrder(depotId, ((depots || []).find((d) => d.depotId === depotId) || {}).settlementAccountId)}
						onCancel={() => setShowOrderForm(false)}
						onDone={() => { setShowOrderForm(false); loadOrders(); }}
					/>
				}
				{orders === null && <p>Lade Orderbuch…</p>}
				{orders !== null && orders.length === 0 && <p className="brokerage-hint">Keine offenen Orders.</p>}
				{orders !== null && orders.length > 0 &&
					<div className="brokerage-order-row brokerage-order-row-header">
						<span>Wertpapier</span>
						<span>Art</span>
						<span>Seite</span>
						<span>Stück</span>
						<span>Handelsplatz</span>
						<span>Limit</span>
						<span>Gültigkeit</span>
						<span>Status</span>
					</div>
				}
				{(orders || []).map((order, idx) => (
					<div key={order.orderId || idx} className="brokerage-order-row">
						<span>{order.instrumentName || order.instrumentId || 'Order'}</span>
						<span>{ORDER_TYPE_LABELS[order.orderType] || order.orderType || ''}</span>
						<span>{order.side === 'SELL' ? 'Verkauf' : 'Kauf'}</span>
						<span>{formatAmount(order.openQuantity || order.quantity)}</span>
						<span>{order.venueName || order.venueId || ''}</span>
						<span>{formatAmount(order.limit)}</span>
						<span>{formatValidity(order)}</span>
						<span>{ORDER_STATUS_LABELS[order.orderStatus] || order.orderStatus || ''}</span>
					</div>
				))}
			</React.Fragment>
		);
	}

	return (
		<Module id={props.id} x={x} classes="module-brokerage-orderbook" title={props.title} display="d-block">
			{connections && connections.length > 1 &&
				<div className="brokerage-connection-switch">
					<label htmlFor="brokerage-orderbook-connection-select">Verbindung</label>
					<select
						id="brokerage-orderbook-connection-select"
						value={connectionId || ''}
						onChange={(e) => switchConnection(parseInt(e.target.value, 10))}
					>
						{connections.map((c) => (
							<option key={c.connectionid} value={c.connectionid}>{c.bankname} – {c.accountlabel}</option>
						))}
					</select>
				</div>
			}
			{inner}
		</Module>
	)

};
